import { Fragment, useLayoutEffect, useRef, useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import type { Comment, HistoryEntry, Me, Person } from '../api.ts';
import { useSave } from '../data.ts';
import { ago, firstName, fmtWhen } from '../util.ts';
import { Avatar, Err, OpsMark } from './ui.tsx';

const AGENT = 'operator@agents.gbsoft.dev';
type Item = { at: string; c?: Comment; h?: HistoryEntry };

// A task's comments and its change log in one timeline, oldest first, with the comment box at the bottom.
export function Activity({ taskId, comments, history, people, me }: { taskId: string; comments: Comment[]; history: HistoryEntry[]; people: Person[]; me: Me }) {
  const [all, setAll] = useState(true);
  const items: Item[] = [...comments.map((c) => ({ at: c.created_at, c })), ...(all ? history.map((h) => ({ at: h.at, h })) : [])].sort((a, b) => a.at.localeCompare(b.at));
  return (
    <section className="side-sec activity">
      <div className="activity-h">
        <h3>Activity</h3>
        <div className="seg sm" role="group" aria-label="Show">
          <button type="button" className={!all ? 'on' : ''} aria-pressed={!all} onClick={() => setAll(false)}>Comments{comments.length ? ` ${comments.length}` : ''}</button>
          <button type="button" className={all ? 'on' : ''} aria-pressed={all} onClick={() => setAll(true)}>All</button>
        </div>
      </div>
      {items.length ? <ol className="feed">{items.map((it) => it.c
        ? <CommentItem key={`c${it.c.id}`} taskId={taskId} c={it.c} people={people} me={me} />
        : <HistoryItem key={`h${it.at}${it.h!.action}`} h={it.h!} people={people} />)}</ol>
        : <p className="muted sm">No comments yet.</p>}
      <Composer taskId={taskId} people={people} />
    </section>
  );
}

function HistoryItem({ h, people }: { h: HistoryEntry; people: Person[] }) {
  const what = h.action === 'create' ? 'created it' : h.action === 'archive' ? 'archived it' : `changed ${Object.keys(h.after ?? {}).filter((k) => k !== 'done_on').join(', ')}`;
  return (
    <li className="feed-h">
      <span><b>{h.actor === AGENT ? 'Ops' : firstName(h.actor, people)}</b>{h.on_behalf_of && <span className="muted"> for {firstName(h.on_behalf_of, people)}</span>} {what} <span className="muted" title={fmtWhen(h.at)}>· {ago(h.at)}</span></span>
      {h.reason && <div className="muted">“{h.reason}”</div>}
    </li>
  );
}

function CommentItem({ taskId, c, people, me }: { taskId: string; c: Comment; people: Person[]; me: Me }) {
  const save = useSave();
  const [edit, setEdit] = useState<string | null>(null);
  const ops = c.author === AGENT;
  const mine = c.author === me.email && !c.on_behalf_of;
  const path = `/tasks/${taskId}/comments/${c.id}`;
  return (
    <li className="feed-c">
      {ops ? <span className="ops-av"><OpsMark size={14} aria-hidden /></span> : <Avatar email={c.author} people={people} size={24} />}
      <div className="feed-body">
        <div className="feed-meta">
          <b>{ops ? 'Ops' : firstName(c.author, people)}</b>
          {ops && c.on_behalf_of && <span className="muted">from {firstName(c.on_behalf_of, people)}'s update</span>}
          <span className="muted" title={fmtWhen(c.created_at)}>{ago(c.created_at)}{c.edited_at ? ' · edited' : ''}</span>
          {mine && edit === null && <span className="feed-tools">
            <button type="button" className="icon" aria-label="Edit comment" onClick={() => setEdit(c.text)}><Pencil size={12} /></button>
            <button type="button" className="icon" aria-label="Delete comment" onClick={() => confirm('Delete this comment?') && save.mutate({ path, method: 'DELETE' })}><Trash2 size={12} /></button>
          </span>}
        </div>
        {edit === null ? <p className="feed-text"><Rich text={c.text} people={people} /></p> : (
          <form onSubmit={async (e) => { e.preventDefault(); if (edit.trim()) { await save.mutateAsync({ path, method: 'PATCH', body: { text: edit } }); setEdit(null); } }}>
            <textarea rows={3} value={edit} onChange={(e) => setEdit(e.target.value)} aria-label="Edit comment" autoFocus
              onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setEdit(null); } if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) e.currentTarget.form?.requestSubmit(); }} />
            <div className="row"><button className="btn sm primary" disabled={!edit.trim() || save.isPending}>Save</button><button type="button" className="btn sm" onClick={() => setEdit(null)}>Cancel</button></div>
          </form>
        )}
        <Err e={save.error} />
      </div>
    </li>
  );
}

// Links open in a new tab; @mentions of people on the team are highlighted.
function Rich({ text, people }: { text: string; people: Person[] }) {
  const names = new Set(people.flatMap((p) => [p.email.toLowerCase(), p.email.split('@')[0].toLowerCase(), (p.name || '').split(' ')[0].toLowerCase()]).filter(Boolean));
  return <>{text.split(/(https?:\/\/[^\s<]+|@[\w.+-]+(?:@[\w.-]+)?)/g).map((part, i) =>
    /^https?:\/\//.test(part) ? <a key={i} href={part} target="_blank" rel="noreferrer">{part}</a>
      : part.startsWith('@') && names.has(part.slice(1).toLowerCase().replace(/[.]+$/, '')) ? <span key={i} className="mention">{part}</span>
      : <Fragment key={i}>{part}</Fragment>)}</>;
}

function Composer({ taskId, people }: { taskId: string; people: Person[] }) {
  const save = useSave();
  const [text, setText] = useState('');
  const [pick, setPick] = useState<{ q: string; at: number; i: number } | null>(null);
  const ref = useRef<HTMLTextAreaElement>(null);
  // Where the caret goes after a mention is inserted, applied once React has rendered the new text.
  const caretTo = useRef<number | null>(null);
  useLayoutEffect(() => { const el = ref.current; if (el && caretTo.current !== null) { el.selectionStart = el.selectionEnd = caretTo.current; caretTo.current = null; } }, [text]);
  const matches = pick ? people.filter((p) => p.active && `${p.name} ${p.email}`.toLowerCase().includes(pick.q.toLowerCase())).slice(0, 5) : [];
  const send = async () => { if (!text.trim() || save.isPending) return; await save.mutateAsync({ path: `/tasks/${taskId}/comments`, body: { text } }); setText(''); };
  const watch = (v: string, caret: number) => {
    const m = /(^|\s)@([\w.]*)$/.exec(v.slice(0, caret));
    setPick(m ? { q: m[2], at: caret - m[2].length - 1, i: 0 } : null);
  };
  const insert = (p: Person) => {
    if (!pick) return;
    const name = (p.name || p.email).split(' ')[0];
    const end = pick.at + 1 + pick.q.length;
    const next = `${text.slice(0, pick.at)}@${name} ${text.slice(end)}`;
    caretTo.current = pick.at + name.length + 2;
    setText(next); setPick(null);
  };
  return (
    <form className="cmt-composer" onSubmit={(e) => { e.preventDefault(); send(); }}>
      <div className="cmt-in">
        <textarea ref={ref} rows={2} value={text} placeholder="Add a comment… @ to mention someone" aria-label="Add a comment"
          onChange={(e) => { setText(e.target.value); watch(e.target.value, e.target.selectionStart); }}
          onKeyDown={(e) => {
            if (pick && matches.length) {
              if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); setPick({ ...pick, i: (pick.i + (e.key === 'ArrowDown' ? 1 : matches.length - 1)) % matches.length }); return; }
              if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insert(matches[pick.i]); return; }
              if (e.key === 'Escape') { e.stopPropagation(); setPick(null); return; }
            }
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); }
          }} />
        {pick && matches.length > 0 && <ul className="mention-menu" role="listbox">{matches.map((p, i) => (
          <li key={p.email} role="option" aria-selected={i === pick.i} className={i === pick.i ? 'on' : ''} onMouseDown={(e) => { e.preventDefault(); insert(p); }}>
            <Avatar email={p.email} people={people} size={18} /> {p.name || p.email}</li>
        ))}</ul>}
      </div>
      <div className="row"><span className="muted sm">⌘/Ctrl + Enter to send</span><span className="spacer" /><button className="btn sm primary" disabled={!text.trim() || save.isPending}>Comment</button></div>
      <Err e={save.error} />
    </form>
  );
}
